import * as React from 'react';
import { cn } from '../lib/cn';
import { Icon } from './icon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export interface FieldHintProps {
  /** Help text revealed in a hover/focus tooltip next to the label. */
  text: string;
  /** Extra classes on the trigger span. */
  className?: string;
}

/**
 * The ONE field-hint affordance: a small muted info icon placed next to a field
 * label whose help text appears in a DS `Tooltip` on hover/focus — instead of a
 * permanent helper line below the control. `Field`, `EditorFormField` and the
 * RHF `FormLabel` all render this, so restyling the DS tooltip (or this icon)
 * updates every form at once (VN4-field-hints).
 *
 * A11y: the trigger is keyboard-focusable (`tabIndex=0`) and exposes the text to
 * screen readers via `role="img"` + `aria-label`; the tooltip opens on hover AND
 * focus.
 */
export const FieldHint = React.forwardRef<HTMLSpanElement, FieldHintProps>(({ text, className }, ref) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref} tabIndex={0} role="img" aria-label={text} className={cn('field-hint', className)}>
          <Icon name="help-circle" size="sm" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
));
FieldHint.displayName = 'FieldHint';
